/*
 * report-view.store.js  —  Engine E (report / section-renderer registry), read side.
 * Purpose: the 'report-view' Vuex module that backs ReportViewScreen — fetch an
 *   interactive report document, expose the section-type -> component registry
 *   (section2cmp), and drive the generate -> poll -> download report flow.
 *
 * reconstructed from lacak.io deployed behavior; app-specific contract verbatim,
 * internal impl re-authored.
 *
 * VERBATIM contract (survives minification; do not rename):
 *   - Vuex module name .............. "report-view"          (registerModuleSafe, ad4f.js)
 *   - action strings ................ report/tracker/retrieve (rootKey "report")   ad4f.js:147
 *                                     report/tracker/generate (rootKey "id")       b509.js:2851
 *                                     report/tracker/status   (rootKey "percent_ready") b509.js:2860
 *                                     report/tracker/download (GET url)            b509.js:2830
 *   - poll interval ................. 200 ms, until percent_ready === 100          b509.js:2857-2868
 *   - stored doc shape .............. { title, sheets:[{ header, sections:[{ type, ... }] }] }
 *   - section2cmp registry (VERBATIM, ad4f.js:126-131):
 *         table    -> report-section-table
 *         text     -> report-section-text
 *         xy_graph -> report-section-xy-graph
 *     (+ 'report-section-not-found' fallback consumed by the screen)
 *   - download GET params ........... { format, add_filename_header, headless:true, report_id, hash }
 *
 * Depends on engine C's shared API substrate (SUBSYSTEMS Pattern C), from
 * ../api/request.js:
 *   request(endpoint, params, resultKey, opts?)  -> Promise (unwraps resultKey)
 *   downloadUrl(endpoint, params)  -> GET url; stamps the session_key cookie as
 *                                     the `hash` query param, "…/endpoint/?…".
 */

import { request, downloadUrl } from '../api/request.js';

// ---------------------------------------------------------------------------
// section `type` string -> Vue component (kebab) name.  [RECOVERED VERBATIM]
// The three concrete section renderers + a graceful fallback (see the screen).
// ---------------------------------------------------------------------------
export const SECTION2CMP = {
  table: 'report-section-table',
  text: 'report-section-text',
  xy_graph: 'report-section-xy-graph',
};

// Fallback component name mounted for any unknown/missing section type.
export const SECTION_FALLBACK = 'report-section-not-found';

// Status poll cadence — VERBATIM 200 ms (b509.js:2857-2868).
const POLL_INTERVAL = 200;

// Factory so the module can be registered per-screen without shared-state leaks.
export function createReportViewModule() {
  return {
    namespaced: true,

    // State: the recovered store keeps `{ report: {} }`; loading + the
    // generate/poll bookkeeping live alongside it.
    state() {
      return {
        report: {},
        loading: false,
        reportId: null,
        percentReady: 0,
      };
    },

    getters: {
      report: (state) => state.report,
      loading: (state) => state.loading,
      // The section registry getter consumed by ReportViewScreen's <component :is>.
      section2cmp: () => SECTION2CMP,
      percentReady: (state) => state.percentReady,
      reportId: (state) => state.reportId,

      // Build the server-side download GET url. downloadUrl() adds the `hash`
      // query param (session_key cookie) itself, yielding the VERBATIM param set
      // { format, add_filename_header, headless:true, report_id, hash }.
      reportDownloadLink: (state) => (opts) => {
        const o = opts || {};
        const reportId = o.report_id != null ? o.report_id : state.reportId;
        return downloadUrl('report/tracker/download', {
          format: o.format,
          add_filename_header: o.add_filename_header,
          headless: true,
          report_id: reportId,
        });
      },
    },

    mutations: {
      setReport(state, report) {
        state.report = report || {};
      },
      setLoading(state, value) {
        state.loading = !!value;
      },
      setReportId(state, id) {
        state.reportId = id;
      },
      // VERBATIM mutation name from the recovered generate flow (b509.js).
      setReportPoolingStatus(state, percent) {
        state.percentReady = percent;
      },
    },

    actions: {
      // Dispatched (not committed) by fetch — mirrors the recovered wiring.
      setLoading({ commit }, value) {
        commit('setLoading', value);
      },
      setReport({ commit }, report) {
        commit('setReport', report);
      },

      // ------------------------------------------------------------------
      // Interactive viewer entry point (ad4f.js:138-160, VERBATIM sequence):
      //   1) setLoading(true)
      //   2) request("report/tracker/retrieve", { report_id }, "report")
      //   3) setReport(result); setLoading(false)
      // ------------------------------------------------------------------
      async fetch({ dispatch }, report_id) {
        dispatch('setLoading', true);
        try {
          const result = await request(
            'report/tracker/retrieve',
            { report_id },
            'report'
          );
          dispatch('setReport', result);
        } finally {
          dispatch('setLoading', false);
        }
      },

      // ------------------------------------------------------------------
      // Build-a-report flow (b509.js:2848-2868):
      //   generate -> report_id, then poll status @200ms until percent_ready 100.
      // ------------------------------------------------------------------
      async generate({ commit, dispatch }, params) {
        const reportId = await request(
          'report/tracker/generate',
          params,
          'id'
        );
        commit('setReportPoolingStatus', 0);
        await dispatch('reportStatusPooling', reportId);
        return reportId;
      },

      reportStatusPooling({ commit }, report_id) {
        return new Promise((resolve, reject) => {
          // Progress-aware watchdog: reject only when the render makes NO forward progress for
          // MAX_STALL consecutive polls (~120s), not after a fixed total poll count. A large
          // report whose percent_ready is slowly-but-steadily climbing is allowed to finish;
          // only a genuinely stuck render (percent frozen) is aborted.
          const MAX_STALL = 600; // ~120s at 200ms with no percent increase
          let stalled = 0;
          let lastPercent = -1;
          const poll = () => {
            request('report/tracker/status', { report_id }, 'percent_ready')
              .then((percent) => {
                commit('setReportPoolingStatus', percent);
                if (percent === 100) {
                  commit('setReportId', report_id);
                  resolve(report_id);
                  return;
                }
                if (percent > lastPercent) { lastPercent = percent; stalled = 0; }
                else if (++stalled > MAX_STALL) { reject(new Error('report generation stalled')); return; }
                setTimeout(poll, POLL_INTERVAL);
              })
              .catch(reject);
          };
          poll();
        });
      },

      // Trigger the server-side render download (pdf|xlsx) via a GET url.
      download({ getters }, opts) {
        const url = getters.reportDownloadLink(opts || {});
        if (typeof window !== 'undefined' && window.open) {
          window.open(url, 'download');
        }
        return url;
      },
    },
  };
}

// Default export: a ready-to-register module instance for the single screen.
const reportViewModule = createReportViewModule();
export default reportViewModule;
